import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarClock,
  CalendarRange,
  Check,
  Megaphone,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { formatINR } from '@/shared/utils/currency';
import { useAnnouncements } from '@/features/announcements/hooks/useAnnouncements';
import { ROUTES } from '@/routes/paths';

interface HrOverview {
  headcount: { total: number; byDepartment: Record<string, number> };
  attendanceToday: { present: number; late: number; onLeave: number; notCheckedIn: number };
  leaves: {
    pendingCount: number;
    pending: {
      id: string;
      employeeUserId: string;
      employee: string;
      department: string;
      type: string;
      fromDate: string;
      toDate: string;
      days: number;
      reason: string;
    }[];
  };
  payroll: { month: string; finalizedCount: number; draftCount: number; finalizedNetTotal: number };
  recruitment: { openRoles: number; draftRoles: number; inPipeline: number };
  recentJoiners: { id: string; name: string; department: string; role: string; joinedAt: string }[];
}

function useHrOverview() {
  return useQuery({
    queryKey: ['hr', 'overview'],
    queryFn: async () => (await api.get<HrOverview>('/api/hr/overview')).data,
    refetchInterval: 60_000,
  });
}

/**
 * Same PATCH /api/leaves/:id LeavesPage.tsx's own review action uses — this
 * card used to only deep-link to that page, so approving one request meant
 * leaving the dashboard entirely just to click the same button that's
 * already visible right here.
 */
function useReviewLeaveInline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'approved' | 'rejected' }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/leaves/${id}`, { status });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'overview'] });
      qc.invalidateQueries({ queryKey: ['leaves'] });
    },
  });
}

const DEPT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  accounts: 'Accounts',
  it: 'IT',
  support: 'Support',
  customer_support: 'Customer Support',
  digital_marketing: 'Digital Marketing',
  management: 'Management',
  unassigned: 'Unassigned',
};

function humanize(value: string): string {
  return DEPT_LABELS[value] ?? value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type StatAccent = 'primary' | 'info' | 'warning' | 'success';

const ACCENT_CLASSES: Record<StatAccent, string> = {
  primary: 'bg-primary/10 text-primary',
  info: 'bg-info/10 text-info-text',
  warning: 'bg-warning/10 text-warning-text',
  success: 'bg-success/10 text-success-text',
};

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
  to,
  linkLabel,
  accent = 'primary',
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subtitle?: string;
  to: string;
  linkLabel: string;
  accent?: StatAccent;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-outline bg-surface p-5 transition-shadow duration-150 hover:shadow-surface-2">
      <div className="flex items-center gap-3">
        <span className={`rounded-lg p-2.5 ${ACCENT_CLASSES[accent]}`}>
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <p className="text-body-sm font-medium text-on-surface-variant">{label}</p>
      </div>
      <p className="text-display-sm font-bold text-on-surface">{value}</p>
      <div className="flex items-center justify-between gap-2">
        {subtitle && <p className="truncate text-body-sm text-on-surface-variant">{subtitle}</p>}
        <Link to={to} className="inline-flex shrink-0 items-center gap-1 text-body-sm font-medium text-primary hover:underline">
          {linkLabel}
          <ArrowRight className="h-3 w-3" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

/**
 * HRMS home for HR users (spec §6): headcount, today's attendance, the leave
 * approval queue, current-month payroll and the recruitment pipeline — every
 * figure computed live from the same collections the section pages manage.
 */
export function HrDashboardPage() {
  const { data, isLoading, isError } = useHrOverview();
  const reviewLeave = useReviewLeaveInline();

  if (isLoading) {
    return (
      <div className="p-4 md:p-6">
        <Skeleton className="h-[600px]" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-6">
        <EmptyState
          title="Could not load the HR dashboard"
          description="This view is limited to HR and administrators. Check your connection or try refreshing."
        />
      </div>
    );
  }

  const departments = Object.entries(data.headcount.byDepartment).sort((a, b) => b[1] - a[1]);
  const maxDept = departments.length ? departments[0][1] : 1;

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-4 md:p-6">
      <PageHeader
        icon={Users}
        eyebrow="People"
        title="HR Dashboard"
        description={<>People, attendance, leave, payroll and hiring — one live view</>}
      />

      {/* ── Headline stats ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Users}
          label="Active Employees"
          value={data.headcount.total}
          subtitle={`Across ${departments.length} department${departments.length !== 1 ? 's' : ''}`}
          to={ROUTES.hr}
          linkLabel="Team & Access"
          accent="primary"
        />
        <StatCard
          icon={CalendarClock}
          label="Present Today"
          value={data.attendanceToday.present}
          subtitle={`${data.attendanceToday.late} late · ${data.attendanceToday.onLeave} on leave · ${data.attendanceToday.notCheckedIn} not checked in`}
          to={ROUTES.attendance}
          linkLabel="Attendance"
          accent="success"
        />
        <StatCard
          icon={CalendarRange}
          label="Pending Leave Approvals"
          value={data.leaves.pendingCount}
          subtitle="Awaiting review"
          to={ROUTES.leaves}
          linkLabel="Leaves"
          accent="warning"
        />
        <StatCard
          icon={BriefcaseBusiness}
          label="Hiring Pipeline"
          value={data.recruitment.inPipeline}
          subtitle={`${data.recruitment.openRoles} open role${data.recruitment.openRoles !== 1 ? 's' : ''}${data.recruitment.draftRoles ? ` · ${data.recruitment.draftRoles} draft` : ''}`}
          to={ROUTES.recruitment}
          linkLabel="Recruitment"
          accent="info"
        />
      </div>

      {/* ── Attendance breakdown (same live figures as the stat card above) ── */}
      <AttendanceBreakdownCard attendance={data.attendanceToday} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* ── Leave approval queue ── */}
        <Card>
          <CardHeader
            title="Leave requests awaiting approval"
            action={
              <Link to={ROUTES.leaves} className="text-body-sm font-medium text-primary hover:underline">
                View all
              </Link>
            }
          />
          <CardBody>
            {data.leaves.pending.length === 0 ? (
              <p className="py-4 text-center text-body-sm text-on-surface-variant">
                No pending requests — the queue is clear.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.leaves.pending.map((leave) => (
                  <li
                    key={leave.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-outline p-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-on-surface">
                        {leave.employeeUserId ? (
                          <Link to={ROUTES.employee360.replace(':userId', encodeURIComponent(leave.employeeUserId))} className="hover:underline">
                            {leave.employee}
                          </Link>
                        ) : (
                          leave.employee
                        )}
                        <span className="ml-2 text-body-sm font-normal text-on-surface-variant">
                          {humanize(leave.department)}
                        </span>
                      </p>
                      <p className="text-body-sm text-on-surface-variant">
                        {leave.fromDate} → {leave.toDate} · {leave.days} day{leave.days !== 1 ? 's' : ''}
                        {leave.reason ? ` · ${leave.reason}` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge tone="warning">{humanize(leave.type)}</Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reviewLeave.isPending}
                        onClick={() =>
                          reviewLeave.mutate(
                            { id: leave.id, status: 'approved' },
                            {
                              onSuccess: () => toast.success(`Approved ${leave.employee}'s leave.`),
                              onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to approve leave.')),
                            },
                          )
                        }
                        aria-label={`Approve ${leave.employee}'s leave`}
                      >
                        <Check className="h-3.5 w-3.5 text-success-text" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reviewLeave.isPending}
                        onClick={() =>
                          reviewLeave.mutate(
                            { id: leave.id, status: 'rejected' },
                            {
                              onSuccess: () => toast.success(`Rejected ${leave.employee}'s leave.`),
                              onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to reject leave.')),
                            },
                          )
                        }
                        aria-label={`Reject ${leave.employee}'s leave`}
                      >
                        <X className="h-3.5 w-3.5 text-danger-text" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* ── Headcount by department ── */}
        <Card>
          <CardHeader
            title="Headcount by department"
            action={
              <Link to={ROUTES.organization} className="text-body-sm font-medium text-primary hover:underline">
                Organization
              </Link>
            }
          />
          <CardBody>
            {departments.length === 0 ? (
              <p className="py-4 text-center text-body-sm text-on-surface-variant">No active employees yet.</p>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {departments.map(([dept, count]) => (
                  <li key={dept} className="flex items-center gap-3">
                    <span className="w-36 shrink-0 truncate text-body-sm text-on-surface">{humanize(dept)}</span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-variant">
                      <span
                        className="block h-full rounded-full bg-primary"
                        style={{ width: `${Math.max(6, (count / maxDept) * 100)}%` }}
                      />
                    </span>
                    <span className="w-8 shrink-0 text-right text-body-sm font-semibold text-on-surface">{count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* ── Payroll this month ── */}
        <Card>
          <CardHeader
            title={`Payroll — ${data.payroll.month}`}
            action={
              <Link to={ROUTES.payroll} className="text-body-sm font-medium text-primary hover:underline">
                Payroll
              </Link>
            }
          />
          <CardBody className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-display-sm font-bold text-on-surface">{data.payroll.finalizedCount}</p>
              <p className="text-body-sm text-on-surface-variant">Finalized payslips</p>
            </div>
            <div>
              <p className="text-display-sm font-bold text-on-surface">{data.payroll.draftCount}</p>
              <p className="text-body-sm text-on-surface-variant">Draft payslips</p>
            </div>
            <div>
              <p className="text-display-sm font-bold text-on-surface">
                {data.payroll.finalizedNetTotal > 0 ? formatINR(data.payroll.finalizedNetTotal) : '—'}
              </p>
              <p className="text-body-sm text-on-surface-variant">Net pay (finalized)</p>
            </div>
          </CardBody>
        </Card>

        {/* ── Recent joiners ── */}
        <Card>
          <CardHeader
            title="Joined in the last 30 days"
            action={
              <Link to={ROUTES.hr} className="text-body-sm font-medium text-primary hover:underline">
                Team & Access
              </Link>
            }
          />
          <CardBody>
            {data.recentJoiners.length === 0 ? (
              <p className="py-4 text-center text-body-sm text-on-surface-variant">No new joiners in the last 30 days.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.recentJoiners.map((joiner) => (
                  <li key={joiner.id} className="flex items-center gap-3 rounded-lg border border-outline p-3">
                    <span className="rounded-full bg-success/10 p-2 text-success-text">
                      <UserPlus className="h-4 w-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-on-surface">
                        <Link to={ROUTES.employee360.replace(':userId', encodeURIComponent(joiner.id))} className="hover:underline">
                          {joiner.name}
                        </Link>
                      </p>
                      <p className="text-body-sm text-on-surface-variant">
                        {humanize(joiner.department)} ·{' '}
                        {new Date(joiner.joinedAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* ── Onboarding Pipeline ── */}
        <OnboardingPipelineCard />

        {/* ── Upcoming Birthdays & Anniversaries ── */}
        <UpcomingEventsCard />
      </div>
    </div>
  );
}

/* ── Attendance Breakdown (stacked bar over the same overview payload) ──── */

function AttendanceBreakdownCard({
  attendance,
}: {
  attendance: HrOverview['attendanceToday'];
}) {
  const total = attendance.present + attendance.onLeave + attendance.notCheckedIn;
  const segments = [
    { key: 'present', label: 'Present', value: attendance.present - attendance.late, color: 'bg-success' },
    { key: 'late', label: 'Late', value: attendance.late, color: 'bg-warning' },
    { key: 'onLeave', label: 'On Leave', value: attendance.onLeave, color: 'bg-info' },
    { key: 'notCheckedIn', label: 'Not Checked In', value: attendance.notCheckedIn, color: 'bg-surface-dim' },
  ].filter((s) => s.value > 0);

  return (
    <Card>
      <CardHeader
        title="Today's attendance breakdown"
        action={
          <Link to={ROUTES.attendance} className="text-body-sm font-medium text-primary hover:underline">
            Attendance
          </Link>
        }
      />
      <CardBody>
        {total === 0 ? (
          <p className="py-4 text-center text-body-sm text-on-surface-variant">No attendance data for today yet.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-variant">
              {segments.map((s) => (
                <div
                  key={s.key}
                  className={`${s.color} transition-all`}
                  style={{ width: `${(s.value / total) * 100}%` }}
                  title={`${s.label}: ${s.value}`}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {segments.map((s) => (
                <div key={s.key} className="flex items-center gap-2 text-body-sm">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${s.color}`} />
                  <span className="truncate text-on-surface-variant">{s.label}</span>
                  <span className="ml-auto font-semibold text-on-surface">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ── Onboarding Pipeline Widget ──────────────────────────────────────────── */

function OnboardingPipelineCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'onboarding-pipeline'],
    queryFn: async () => {
      const { data: result } = await api.get('/api/hr-onboarding-candidates/candidates?limit=200');
      const candidates = result?.data || [];
      const counts: Record<string, number> = { pending_info: 0, info_submitted: 0, docs_submitted: 0, verification: 0, completed: 0 };
      for (const c of candidates) { if (counts[c.onboardingStatus] !== undefined) counts[c.onboardingStatus]++; else counts.pending_info++; }
      return { total: candidates.length, ...counts };
    },
    staleTime: 30_000,
  });

  const stages = [
    { key: 'pending_info', label: 'Pending Info', color: 'bg-gray-400' },
    { key: 'info_submitted', label: 'Info Submitted', color: 'bg-info' },
    { key: 'docs_submitted', label: 'Docs Submitted', color: 'bg-indigo-500' },
    { key: 'verification', label: 'Verification', color: 'bg-purple-500' },
    { key: 'completed', label: 'Completed', color: 'bg-success' },
  ];

  return (
    <Card>
      <CardHeader
        title="Onboarding Pipeline"
        action={
          <Link to={ROUTES.hrOnboarding} className="text-body-sm font-medium text-primary hover:underline">
            View all
          </Link>
        }
      />
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-[100px]" />
        ) : !data || data.total === 0 ? (
          <p className="py-4 text-center text-body-sm text-on-surface-variant">No onboarding candidates yet. Hire someone from Recruitment to see them here.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-body-sm text-on-surface-variant">{data.total} candidate{data.total !== 1 ? 's' : ''} in pipeline</p>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-variant">
              {stages.map(s => {
                const count = (data as Record<string, number>)[s.key] || 0;
                const pct = data.total > 0 ? (count / data.total) * 100 : 0;
                return pct > 0 ? <div key={s.key} className={`${s.color} transition-all`} style={{ width: `${pct}%` }} title={`${s.label}: ${count}`} /> : null;
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {stages.map(s => {
                const count = (data as Record<string, number>)[s.key] || 0;
                return (
                  <div key={s.key} className="flex items-center gap-2 text-body-sm">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                    <span className="text-on-surface-variant">{s.label}</span>
                    <span className="ml-auto font-semibold text-on-surface">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ── Upcoming Birthdays, Work Anniversaries & Holidays ───────────────────── */

function UpcomingEventsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'upcoming-events'],
    queryFn: async () => {
      const { data: result } = await api.get('/api/hr-performance/upcoming-events');
      return result as {
        birthdays: { name: string; date: string; department: string }[];
        anniversaries: { name: string; date: string; years: number; department: string }[];
        holidays: { name: string; date: string }[];
      };
    },
    staleTime: 5 * 60_000,
  });

  const { data: announcements } = useAnnouncements();
  const updates = announcements?.slice(0, 5) ?? [];

  return (
    <Card>
      <CardHeader title="Upcoming Events (Next 30 Days)" />
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-[150px]" />
        ) : !data ? (
          <p className="py-4 text-center text-body-sm text-on-surface-variant">Could not load events.</p>
        ) : (
          <div className="space-y-4">
            {/* Upcoming Updates (Announcements) */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5 flex items-center gap-1.5">
                <Megaphone className="h-4 w-4 text-primary" aria-hidden /> Upcoming Updates
              </p>
              {updates.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No recent announcements.</p>
              ) : (
                <ul className="space-y-1">
                  {updates.map((update) => (
                    <li key={update.id} className="flex items-center justify-between gap-2 text-body-sm">
                      <span className="min-w-0 truncate text-on-surface">{update.title}</span>
                      <span className="shrink-0 text-on-surface-variant">
                        {new Date(update.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Birthdays */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5">🎂 Upcoming Birthdays</p>
              {data.birthdays.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No upcoming birthdays.</p>
              ) : (
                <ul className="space-y-1">
                  {data.birthdays.map((b, i) => (
                    <li key={i} className="flex items-center justify-between text-body-sm">
                      <span className="text-on-surface">{b.name} <span className="text-on-surface-variant">({b.department})</span></span>
                      <span className="text-on-surface-variant">{new Date(b.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Anniversaries */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5">🎉 Work Anniversaries</p>
              {data.anniversaries.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No upcoming anniversaries.</p>
              ) : (
                <ul className="space-y-1">
                  {data.anniversaries.map((a, i) => (
                    <li key={i} className="flex items-center justify-between text-body-sm">
                      <span className="text-on-surface">{a.name} <span className="text-on-surface-variant">({a.years} year{a.years !== 1 ? 's' : ''})</span></span>
                      <span className="text-on-surface-variant">{new Date(a.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Holidays */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5">🏖️ Upcoming Holidays</p>
              {data.holidays.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No upcoming holidays.</p>
              ) : (
                <ul className="space-y-1">
                  {data.holidays.map((h, i) => (
                    <li key={i} className="flex items-center justify-between text-body-sm">
                      <span className="text-on-surface">{h.name}</span>
                      <span className="text-on-surface-variant">{new Date(h.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', weekday: 'short' })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
